/**
 * `messaging` endpoint wrappers.
 *
 * Field shapes are derived from the messaging serializers — see
 * src/types/messaging.ts for the full derivation notes. Route paths and
 * ordering are verified against refactor/backend/src/modules/messaging/routes.ts.
 *
 * `createConversation` can 403 with
 * "You can only start conversations with your own landlord, agent, or
 * tenants." — see contacts.ts. Do not try to reconstruct that rule
 * client-side; just call this and surface the backend's message on failure
 * via parseApiError.
 *
 * `broadcasts/` wrappers are gated server-side to manager roles for EVERY
 * method, including GET — see `requireManagerRole` in routes.ts. A tenant
 * calling these gets 403.
 */
import { apiClient, uploadFile } from '../client';
import { MESSAGING_BROADCASTS, MESSAGING_CONVERSATIONS } from './paths';
import type { Paginated } from '../types';
import type {
  BroadcastMessage,
  BroadcastMessageCreateRequest,
  BroadcastMessagePatchRequest,
  Conversation,
  ConversationCreateRequest,
  ConversationPatchRequest,
  ConversationRow,
  Message,
  SendMessageRequest,
} from '../../types/messaging';

// ---------------------------------------------------------------------------
// conversations/
// ---------------------------------------------------------------------------

/** `GET messaging/conversations/` — paginated, scoped to the caller's own conversations. */
export async function listConversations(params?: {
  page?: number | undefined;
}): Promise<Paginated<ConversationRow>> {
  const response = await apiClient.get<Paginated<ConversationRow>>(MESSAGING_CONVERSATIONS, {
    params,
  });
  return response.data;
}

/** `GET messaging/conversations/{id}/` — full ordered message thread. */
export async function getConversation(id: number): Promise<Conversation> {
  const response = await apiClient.get<Conversation>(`${MESSAGING_CONVERSATIONS}${id}/`);
  return response.data;
}

/**
 * `POST messaging/conversations/` — the caller is always added as a
 * participant server-side, whether or not they included their own id. Can
 * 403 if any requested participant is outside `allowedContactIds(user)`.
 */
export async function createConversation(payload: ConversationCreateRequest): Promise<Conversation> {
  const response = await apiClient.post<Conversation>(MESSAGING_CONVERSATIONS, payload);
  return response.data;
}

/**
 * `POST messaging/conversations/{id}/send_message/` — bespoke `{text, image}`
 * body, not the generic write schema. At least one of `text`/`image` must be
 * non-empty or the backend 400s with `{error: "Message text or image is required"}`.
 */
export async function sendMessage(id: number, payload: SendMessageRequest): Promise<Message> {
  const response = await apiClient.post<Message>(
    `${MESSAGING_CONVERSATIONS}${id}/send_message/`,
    payload,
  );
  return response.data;
}

/**
 * `POST messaging/conversations/{id}/send_message/` — multipart variant, for
 * an actual image attachment rather than an already-stored path. Same
 * text-or-image-required rule as `sendMessage`.
 */
export async function sendMessageImage(
  id: number,
  payload: { text?: string; image: File },
): Promise<Message> {
  const formData = new FormData();
  if (payload.text !== undefined && payload.text !== '') formData.append('text', payload.text);
  formData.append('image', payload.image);
  return uploadFile<Message>(`${MESSAGING_CONVERSATIONS}${id}/send_message/`, formData);
}

/**
 * `PATCH messaging/conversations/{id}/`. When `participants` is provided this
 * REPLACES the whole participant set with no `allowedContactIds` re-check —
 * documented Django gap, ported as-is.
 */
export async function updateConversation(
  id: number,
  payload: ConversationPatchRequest,
): Promise<Conversation> {
  const response = await apiClient.patch<Conversation>(`${MESSAGING_CONVERSATIONS}${id}/`, payload);
  return response.data;
}

/** `DELETE messaging/conversations/{id}/`. */
export async function deleteConversation(id: number): Promise<void> {
  await apiClient.delete(`${MESSAGING_CONVERSATIONS}${id}/`);
}

// ---------------------------------------------------------------------------
// broadcasts/ — manager roles only, every method
// ---------------------------------------------------------------------------

/** `GET messaging/broadcasts/` — paginated, scoped to broadcasts the caller sent. */
export async function listBroadcasts(params?: {
  page?: number | undefined;
}): Promise<Paginated<BroadcastMessage>> {
  const response = await apiClient.get<Paginated<BroadcastMessage>>(MESSAGING_BROADCASTS, { params });
  return response.data;
}

/** `GET messaging/broadcasts/{id}/`. */
export async function getBroadcast(id: number): Promise<BroadcastMessage> {
  const response = await apiClient.get<BroadcastMessage>(`${MESSAGING_BROADCASTS}${id}/`);
  return response.data;
}

/**
 * `POST messaging/broadcasts/` — a documented no-op server-side (no FCM push
 * fires); the row is still created and readable afterward.
 */
export async function createBroadcast(payload: BroadcastMessageCreateRequest): Promise<BroadcastMessage> {
  const response = await apiClient.post<BroadcastMessage>(MESSAGING_BROADCASTS, payload);
  return response.data;
}

/** `PATCH messaging/broadcasts/{id}/`. */
export async function updateBroadcast(
  id: number,
  payload: BroadcastMessagePatchRequest,
): Promise<BroadcastMessage> {
  const response = await apiClient.patch<BroadcastMessage>(`${MESSAGING_BROADCASTS}${id}/`, payload);
  return response.data;
}

/** `DELETE messaging/broadcasts/{id}/`. */
export async function deleteBroadcast(id: number): Promise<void> {
  await apiClient.delete(`${MESSAGING_BROADCASTS}${id}/`);
}
